// Endpoints RTK Query da feature CRM. O back devolve um payload ENXUTO; aqui
// transformamos pro view-model que a UI (1:1 com o design) precisa, derivando o
// que o contrato não traz (company, avatarTone, labels de estado/evento).
//
// Tipos manuais espelhando os DTOs reais de chatia-api/src/modules/crm. Quando
// openapi-typescript regerar generated.ts, migrar pra paths['/crm/...'].
// FIXME: API local indisponível na build inicial — validado contra MSW.
import { api } from '@/lib/api';
import type { CursorPage } from '@/features/stores';
import type { AvatarTone } from '@/components/ui/Avatar';
import type { CrmSegment } from './utils/segment';
import { toneFromId } from './utils/tone';

// ============================================================================
// Raw (contrato do back) — o que sai dos serviços de chatia-api.
// ============================================================================

interface RawOwner {
  id: string;
  name: string;
  avatarTone: number;
}

interface RawCarteiraItem {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  storeId: string | null;
  /** Pode ser null até o job de segmentação rodar. */
  segment: CrmSegment | null;
  csat: number | null;
  ltvCents: number;
  ticketCents: number;
  orderCount: number;
  lastOrderAt: string | null;
  owner: RawOwner | null;
  produtos: string[];
  version: number;
}

interface RawHistoryItem {
  date: string;
  product: string;
  /** OrderStatus | QuoteStatus do Prisma. */
  state: string;
  valueCents: number;
  kind: 'order' | 'quote';
}

interface RawClienteDetail {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  storeId: string | null;
  segment: CrmSegment | null;
  csat: number | null;
  owner: RawOwner | null;
  produtos: string[];
  notes?: string | null;
  ltvCents: number;
  ticketCents: number;
  orderCount: number;
  lastOrderAt: string | null;
  version: number;
  history: RawHistoryItem[];
}

interface RawFollowUpItem {
  quoteId: string;
  contactId: string;
  contactName: string;
  storeId: string;
  valueCents: number;
  status: FollowUpStatus;
  sentAt: string;
  coolingDown: boolean;
}

interface RawCarteiraSummary {
  totalClientes: number;
  receitaCents: number;
  ticketMedioCents: number;
  taxaRecompra: number;
  porLoja: { storeId: string; storeName: string; totalClientes: number; receitaCents: number }[];
}

interface RawTemplateStep {
  dayOffset: number;
  body: string;
}

interface RawTemplate {
  key: TemplateKey;
  type: string;
  name: string;
  description: string;
  steps: RawTemplateStep[];
}

/** Tom da IA ao conduzir a resposta (espelha enum Tone do Prisma). */
export type Tone = 'FORMAL' | 'CALOROSO' | 'DIRETO';

/** O que a IA faz quando o cliente responde (enum CampaignReplyBehavior). */
export type ReplyBehavior = 'STOP' | 'HANDOFF' | 'AI_CONTINUE';

/** Estado de um alvo no funil (enum CampaignTargetStatus). */
export type CampaignTargetStatus =
  | 'PENDING'
  | 'SENDING'
  | 'SENT'
  | 'REPLIED'
  | 'STOPPED'
  | 'FAILED';

/** Estado da campanha (enum CampaignStatus). DRAFT/DONE existem mas a UI só age em ACTIVE/PAUSED/CANCELLED. */
export type CampaignStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'DONE' | 'CANCELLED';

interface RawCampaign {
  id: string;
  name: string;
  type: string;
  templateKey: TemplateKey;
  status: CampaignStatus;
  storeId: string | null;
  stopOnReply: boolean;
  discountPct: number | null;
  aiTone: Tone | null;
  objective: string | null;
  replyBehavior: ReplyBehavior;
  /** null nas campanhas anteriores ao rascunho persistido (critério não guardado). */
  audienceFilter: AudienceFilter | null;
  storeChosen: boolean;
  targetCount: number;
  createdAt: string;
  version: number;
}

interface RawAudienceContacts {
  data: RawCarteiraItem[];
  total: number;
  page: number;
  pageCount: number;
}

type RawCampaignDetail = RawCampaign & {
  breakdown: Record<CampaignTargetStatus, number>;
  storeName: string | null;
  createdBy: { id: string; name: string } | null;
  updatedAt: string;
  templateSteps: { dayOffset: number; body: string }[];
};

interface RawCampaignTarget {
  id: string;
  status: CampaignTargetStatus;
  currentStep: number;
  nextSendAt: string | null;
  lastEventAt: string | null;
  contact: RawCarteiraItem;
  conversationId: string | null;
}

interface RawAudience {
  count: number;
  preview: RawCarteiraItem[];
  contactIds: string[];
  truncated: boolean;
}

type Envelope<T> = { data: T[]; pageInfo?: { nextCursor: string | null; hasMore: boolean } };

// ============================================================================
// View-model (consumido pela UI).
// ============================================================================

export interface CrmOwner {
  id: string;
  name: string;
  avatarTone: AvatarTone;
}

export type CarteiraSort = 'ltv' | 'pedidos' | 'ticket' | 'recente';

export interface CrmClient {
  id: string;
  name: string;
  company: string | null;
  segment: CrmSegment;
  ltvCents: number;
  ticketCents: number;
  orderCount: number;
  lastOrderAt: string | null;
  owner: CrmOwner | null;
  produtos: string[];
  storeId: string;
  avatarTone: AvatarTone;
  /**
   * Versão do optimistic lock, vinda da listagem. OBRIGATÓRIA de propósito: quando
   * era opcional, o Kanban caía no `?? 0` e o PATCH batia com 0 contra a version
   * real → 409 STALE_ENTITY em TODO contato já editado, e o "tente de novo" do toast
   * era impossível de cumprir (mandava 0 outra vez).
   */
  version: number;
}

export interface ListCarteiraArgs {
  segment?: CrmSegment | 'all';
  storeId?: string;
  q?: string;
  sort?: CarteiraSort;
  cursor?: string;
  limit?: number;
}

export interface CarteiraSummaryStore {
  storeId: string;
  nome: string;
  clientes: number;
  receitaCents: number;
}

export interface CarteiraSummary {
  totalClientes: number;
  receitaCents: number;
  ticketMedioCents: number;
  taxaRecompra: number;
  porLoja: CarteiraSummaryStore[];
}

export type HistoryState = 'orcamento' | 'producao' | 'entregue';

export interface CrmHistoryItem {
  date: string;
  product: string;
  valueCents: number;
  state: HistoryState;
}

export interface CrmClientDetail {
  id: string;
  name: string;
  company: string | null;
  contact: string;
  phone: string | null;
  segment: CrmSegment;
  storeId: string | null;
  avatarTone: AvatarTone;
  owner: CrmOwner | null;
  csat: number | null;
  sinceLabel: string | null;
  ltvCents: number;
  ticketCents: number;
  orderCount: number;
  lastOrderAt: string | null;
  produtos: string[];
  note: string | null;
  history: CrmHistoryItem[];
  version: number;
}

export interface CrmStaleEntityError {
  code: 'STALE_ENTITY';
}

export interface PatchClientInput {
  id: string;
  version: number;
  crmSegment?: CrmSegment;
  ownerId?: string;
}

export type FollowUpStatus = 'nao_visto' | 'visto' | 'negociando';

export interface FollowUpItem {
  id: string;
  clientId: string;
  clientName: string;
  contact: string;
  company: string | null;
  storeId: string;
  avatarTone: AvatarTone;
  product: string;
  valueCents: number;
  sentDays: number;
  status: FollowUpStatus;
  lastEvent: string;
}

export interface FollowUpSummary {
  abertos: number;
  valorEmJogoCents: number;
  esfriando: number;
  naoVisualizados: number;
}

export interface ReactivateItem {
  id: string;
  name: string;
  company: string | null;
  contact: string;
  segment: CrmSegment;
  storeId: string;
  avatarTone: AvatarTone;
  owner: CrmOwner | null;
  ltvCents: number;
  orderCount: number;
  lastOrderAt: string | null;
  produtos: string[];
}

export type ReactivateGroup = 'todos' | 'nunca' | 'pararam';

export interface ReactivateSummary {
  aReativar: number;
  nuncaCompraram: number;
  pararam: number;
  valorPerdidoCents: number;
}

export type TemplateKey = 'followup' | 'reativar_inativo' | 'reativar_lead' | 'desconto';

export interface TemplateStep {
  day: string;
  tone: string;
  text: string;
}

export interface CampaignTemplate {
  title: string;
  goal: string;
  cadence: TemplateStep[];
}

export type CrmTemplates = Record<TemplateKey, CampaignTemplate>;

/** Alvo no POST — o back espera um único array `targets`. */
export interface CampaignTargetInput {
  contactId: string;
  quoteId?: string;
}

/**
 * POST /crm/campaigns — abre a campanha como RASCUNHO (não dispara).
 *
 * Tudo é opcional: "Nova Campanha" manda `{}` e as decisões vêm nos passos
 * seguintes, cada um com seu PATCH. O corpo cheio continua aceito para quem monta
 * a campanha inteira de uma vez (scripts, integrações) e chama /activate depois.
 */
export interface CreateCampaignInput {
  templateKey?: TemplateKey;
  /** Nome amigável. Ausente = nome do template. */
  name?: string;
  stopOnReply?: boolean;
  storeId?: string;
  /**
   * Número de WhatsApp por onde os disparos saem. Ausente = o principal da loja no
   * momento de cada envio (não congelado na criação). Exige `storeId`: campanha de
   * rede não fixa número, porque cada contato cai na loja dele.
   */
  storeWhatsappNumberId?: string;
  /** % de desconto (1–90). Obrigatório quando templateKey === 'desconto'. */
  discountPct?: number;
  /** Tom da IA. Ausente = tom da loja. */
  aiTone?: Tone;
  /** Objetivo curto injetado no prompt do agente. */
  objective?: string;
  /** O que fazer quando o cliente responde. */
  replyBehavior?: ReplyBehavior;
  targets?: CampaignTargetInput[];
}

export interface Campaign {
  id: string;
  name: string;
  type: string;
  templateKey: TemplateKey;
  status: CampaignStatus;
  storeId: string | null;
  stopOnReply: boolean;
  discountPct: number | null;
  aiTone: Tone | null;
  objective: string | null;
  replyBehavior: ReplyBehavior;
  /**
   * Critério que gerou o público. null = campanha do fluxo antigo, em que o
   * público existe só como lista de alvos e o "por quê" se perdeu.
   */
  audienceFilter: AudienceFilter | null;
  /**
   * A loja já foi decidida no builder. Desambigua `storeId: null`, que significa
   * tanto "campanha de rede" quanto "rascunho que ainda não chegou nesse passo" —
   * sem isso um rascunho novo abriria com "Rede toda" já marcada.
   */
  storeChosen: boolean;
  targetCount: number;
  createdAt: string;
  version: number;
}

export type CampaignBreakdown = Record<CampaignTargetStatus, number>;

/**
 * Detalhe do que foi aplicado. Cobre só o que está persistido: os filtros de
 * público usados na criação não são gravados pelo backend, então não voltam aqui.
 */
export interface CampaignDetail extends Campaign {
  breakdown: CampaignBreakdown;
  /** Nome da loja de origem. null = campanha de rede. */
  storeName: string | null;
  createdBy: { id: string; name: string } | null;
  updatedAt: string;
  /** Cadência aplicada. Reflete o template atual, não um snapshot do disparo. */
  templateSteps: { dayOffset: number; body: string }[];
}

/** Sentinela do filtro multi-loja p/ campanhas de rede (storeId null). */
export const NETWORK_STORE = 'network';

/** Filtro da listagem (GET /crm/campaigns). Lista vazia/ausente = todas as lojas. */
export interface ListCampaignsInput {
  storeIds?: string[];
}

/** Alvo no monitor: contato (view-model da carteira) + estado de disparo. */
export interface CampaignTarget {
  id: string;
  status: CampaignTargetStatus;
  currentStep: number;
  nextSendAt: string | null;
  lastEventAt: string | null;
  client: CrmClient;
  conversationId: string | null;
}

// ---------------------------------------------------------------------------
// Critério de público — espelha crm.audience-filter.ts no back.
//
// Árvore de regras versionada, e não um objeto plano: os critérios de público
// crescem por adição e precisam combinar por E/OU com agrupamento. Critério novo é
// um `kind` novo aqui e um case no tradutor do back — nada de migration.
// ---------------------------------------------------------------------------

export const AUDIENCE_FILTER_VERSION = 1;

export type AudienceCriterion =
  | { kind: 'segment'; in: CrmSegment[] }
  | { kind: 'boughtWithinDays'; days: number }
  | { kind: 'notOrderedForDays'; days: number };

export interface AudienceSubGroup {
  op: 'AND' | 'OR';
  rules: AudienceCriterion[];
}

export interface AudienceGroup {
  op: 'AND' | 'OR';
  rules: (AudienceCriterion | AudienceSubGroup)[];
}

export interface AudienceFilter {
  version: typeof AUDIENCE_FILTER_VERSION;
  /** `static` congela a lista confirmada; `dynamic` reavalia a cada disparo. */
  mode: 'static' | 'dynamic';
  include: AudienceGroup;
  exclude?: AudienceGroup | null;
  /** Contatos tirados a mão na confirmação do público. */
  removedContactIds: string[];
}

export function emptyAudienceFilter(): AudienceFilter {
  return {
    version: AUDIENCE_FILTER_VERSION,
    mode: 'static',
    include: { op: 'AND', rules: [] },
    exclude: null,
    removedContactIds: [],
  };
}

/** Filtros do builder de público (POST /crm/campaigns/audience). */
export interface BuildAudienceInput {
  /** `followup` sai de Quote (orçamento em aberto) e ignora segmento/recência. */
  source: 'carteira' | 'followup';
  /** Critério declarativo. Quando presente, substitui os campos planos abaixo. */
  filter?: AudienceFilter;
  /** Segmentos do público. Ausente/vazio = todos. */
  segments?: CrmSegment[];
  storeId?: string;
  boughtWithinDays?: number;
  notOrderedForDays?: number;
}

export interface Audience {
  count: number;
  preview: CrmClient[];
  contactIds: string[];
  truncated: boolean;
}

/** POST /crm/campaigns/audience/contacts — o público inteiro, 20 por página. */
export interface AudienceContactsInput {
  contactIds: string[];
  q?: string;
  page?: number;
  limit?: number;
}

export interface AudienceContactsPage {
  items: CrmClient[];
  total: number;
  page: number;
  pageCount: number;
}

/** PATCH /crm/campaigns/:id — pausar/retomar/cancelar (optimistic lock). */
export interface PatchCampaignStatusInput {
  id: string;
  version: number;
  status: 'ACTIVE' | 'PAUSED' | 'CANCELLED';
}

// --- Passos do builder (rascunho persistido) --------------------------------
// Uma rota por passo: cada um tem regra própria no back (trocar a loja invalida o
// público, trocar o público revalida os contatos, trocar a estratégia re-deriva o
// tipo da campanha).

export interface PatchCampaignStoreInput {
  id: string;
  version: number;
  /** null = campanha de rede (sem loja). */
  storeId?: string | null;
  storeWhatsappNumberId?: string | null;
}

export interface PatchCampaignAudienceInput {
  id: string;
  version: number;
  filter: AudienceFilter;
  contactIds: string[];
}

export interface PatchCampaignStrategyInput {
  id: string;
  version: number;
  templateKey: TemplateKey;
  name?: string;
  objective?: string | null;
  aiTone?: Tone | null;
  replyBehavior: ReplyBehavior;
  discountPct?: number | null;
  stopOnReply: boolean;
}

export interface ActivateCampaignInput {
  id: string;
  version: number;
}

// ============================================================================
// Transforms (raw → view-model). Derivam o que o contrato não entrega.
// ============================================================================

/** Segmento ausente = lead ainda não classificado pelo job. */
function normSegment(segment: CrmSegment | null): CrmSegment {
  return segment ?? 'LEAD';
}

function toOwner(owner: RawOwner | null): CrmOwner | null {
  return owner ? { ...owner, avatarTone: toneFromId(owner.id) } : null;
}

function toClient(raw: RawCarteiraItem): CrmClient {
  return {
    id: raw.id,
    name: raw.name,
    // O back guarda só `name` no contato. Sem campo de empresa separado, o nome
    // é o rótulo principal; ClientCell usa `company ?? name`.
    company: null,
    segment: normSegment(raw.segment),
    ltvCents: raw.ltvCents,
    ticketCents: raw.ticketCents,
    orderCount: raw.orderCount,
    lastOrderAt: raw.lastOrderAt,
    owner: toOwner(raw.owner),
    produtos: raw.produtos,
    storeId: raw.storeId ?? '',
    avatarTone: toneFromId(raw.id),
    version: raw.version,
  };
}

function toReactivate(raw: RawCarteiraItem): ReactivateItem {
  return {
    id: raw.id,
    name: raw.name,
    company: null,
    contact: raw.name,
    segment: normSegment(raw.segment),
    storeId: raw.storeId ?? '',
    avatarTone: toneFromId(raw.id),
    owner: toOwner(raw.owner),
    ltvCents: raw.ltvCents,
    orderCount: raw.orderCount,
    lastOrderAt: raw.lastOrderAt,
    produtos: raw.produtos,
  };
}

const QUOTE_LIKE = new Set([
  'DRAFT',
  'PENDING_APPROVAL',
  'SENT',
  'APPROVED',
  'REJECTED',
  'EXPIRED',
]);
const DELIVERED = new Set(['DELIVERED', 'READY']);

function toHistoryState(state: string, kind: 'order' | 'quote'): HistoryState {
  if (kind === 'quote') return 'orcamento';
  if (DELIVERED.has(state)) return 'entregue';
  if (QUOTE_LIKE.has(state)) return 'orcamento';
  return 'producao';
}

/** ISO → "04 jun" (dia + mês curto pt-BR), como o design mostra no histórico. */
function formatHistoryDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace('.', '');
}

function toDetail(raw: RawClienteDetail): CrmClientDetail {
  return {
    id: raw.id,
    name: raw.name,
    company: null,
    contact: raw.name,
    phone: raw.phone,
    segment: normSegment(raw.segment),
    storeId: raw.storeId,
    avatarTone: toneFromId(raw.id),
    owner: toOwner(raw.owner),
    csat: raw.csat,
    sinceLabel: null,
    ltvCents: raw.ltvCents,
    ticketCents: raw.ticketCents,
    orderCount: raw.orderCount,
    lastOrderAt: raw.lastOrderAt,
    produtos: raw.produtos,
    note: raw.notes ?? null,
    version: raw.version,
    history: raw.history.map((h) => ({
      date: formatHistoryDate(h.date),
      product: h.product,
      valueCents: h.valueCents,
      state: toHistoryState(h.state, h.kind),
    })),
  };
}

const FOLLOWUP_EVENT: Record<FollowUpStatus, string> = {
  nao_visto: 'Entregue, ainda não visualizou',
  visto: 'Visualizou, sem resposta',
  negociando: 'Respondeu, em negociação',
};

function daysBetween(iso: string): number {
  const ms = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
}

function toFollowUp(raw: RawFollowUpItem): FollowUpItem {
  return {
    id: raw.quoteId,
    clientId: raw.contactId,
    clientName: raw.contactName,
    contact: raw.contactName,
    company: raw.contactName,
    storeId: raw.storeId,
    avatarTone: toneFromId(raw.contactId),
    // O contrato do follow-up não traz o nome do produto — rótulo genérico.
    product: 'Orçamento enviado',
    valueCents: raw.valueCents,
    sentDays: daysBetween(raw.sentAt),
    status: raw.status,
    lastEvent: FOLLOWUP_EVENT[raw.status],
  };
}

function toSummary(raw: RawCarteiraSummary): CarteiraSummary {
  return {
    totalClientes: raw.totalClientes,
    receitaCents: raw.receitaCents,
    ticketMedioCents: raw.ticketMedioCents,
    taxaRecompra: raw.taxaRecompra,
    porLoja: raw.porLoja.map((s) => ({
      storeId: s.storeId,
      nome: s.storeName.replace('Print Pro · ', ''),
      clientes: s.totalClientes,
      receitaCents: s.receitaCents,
    })),
  };
}

const TEMPLATE_GOAL: Record<TemplateKey, string> = {
  followup: 'Reaquecer quem recebeu orçamento e não respondeu',
  reativar_inativo: 'Trazer de volta quem parou de comprar',
  reativar_lead: 'Converter quem chegou mas nunca comprou',
  desconto: 'Oferecer um desconto pontual para o segmento',
};

const STEP_TONE = ['Lembrete amigável', 'Reforço de valor', 'Última chamada'];

function toTemplates(raw: Envelope<RawTemplate>): CrmTemplates {
  const out = {} as CrmTemplates;
  for (const t of raw.data) {
    out[t.key] = {
      title: t.name,
      goal: TEMPLATE_GOAL[t.key] ?? t.description,
      cadence: t.steps.map((s, i) => ({
        day: `Dia ${s.dayOffset}`,
        tone: STEP_TONE[i] ?? `Toque ${i + 1}`,
        text: s.body,
      })),
    };
  }
  return out;
}

function toCampaign(raw: RawCampaign): Campaign {
  return {
    id: raw.id,
    name: raw.name,
    type: raw.type,
    templateKey: raw.templateKey,
    status: raw.status,
    storeId: raw.storeId,
    stopOnReply: raw.stopOnReply,
    discountPct: raw.discountPct,
    aiTone: raw.aiTone,
    objective: raw.objective,
    replyBehavior: raw.replyBehavior,
    audienceFilter: raw.audienceFilter ?? null,
    storeChosen: raw.storeChosen ?? false,
    targetCount: raw.targetCount,
    createdAt: raw.createdAt,
    version: raw.version,
  };
}

/** Tags invalidadas por qualquer gravação numa campanha: o item e a lista. */
function campaignTags(id: string) {
  return [
    { type: 'CrmCampaign' as const, id },
    { type: 'CrmCampaign' as const, id: 'LIST' },
  ];
}

function toCampaignDetail(raw: RawCampaignDetail): CampaignDetail {
  return {
    ...toCampaign(raw),
    breakdown: raw.breakdown,
    storeName: raw.storeName,
    createdBy: raw.createdBy,
    updatedAt: raw.updatedAt,
    templateSteps: raw.templateSteps,
  };
}

function toCampaignTarget(raw: RawCampaignTarget): CampaignTarget {
  return {
    id: raw.id,
    status: raw.status,
    currentStep: raw.currentStep,
    nextSendAt: raw.nextSendAt,
    lastEventAt: raw.lastEventAt,
    client: toClient(raw.contact),
    conversationId: raw.conversationId,
  };
}

function toAudience(raw: RawAudience): Audience {
  return {
    count: raw.count,
    preview: raw.preview.map(toClient),
    contactIds: raw.contactIds,
    truncated: raw.truncated,
  };
}

// ============================================================================

function qs(params: Record<string, string | number | undefined>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '' || v === 'all') continue;
    parts.push(`${k}=${encodeURIComponent(String(v))}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

function toCursorPage<T, R>(raw: Envelope<R>, map: (r: R) => T): CursorPage<T> {
  return { items: raw.data.map(map), nextCursor: raw.pageInfo?.nextCursor ?? null };
}

// ============================================================================
// Quadros kanban personalizados (CRM > Quadros)
// ============================================================================

interface RawBoardSummary {
  id: string;
  name: string;
  position: number;
  version: number;
  columnCount: number;
  cardCount: number;
}

interface RawBoardCard {
  cardId: string;
  columnId: string;
  position: number;
  version: number;
  contact: RawCarteiraItem;
}

interface RawBoardColumn {
  id: string;
  name: string;
  color: string | null;
  position: number;
  version: number;
  cards: RawBoardCard[];
}

interface RawBoard {
  id: string;
  name: string;
  position: number;
  version: number;
  columns: RawBoardColumn[];
}

export interface BoardSummary {
  id: string;
  name: string;
  position: number;
  version: number;
  columnCount: number;
  cardCount: number;
}

/** Card = placement de um cliente numa coluna. `client` reusa o view-model da carteira. */
export interface BoardCard {
  cardId: string;
  columnId: string;
  position: number;
  version: number;
  client: CrmClient;
}

export interface BoardColumn {
  id: string;
  name: string;
  color: string | null;
  position: number;
  version: number;
  cards: BoardCard[];
}

export interface Board {
  id: string;
  name: string;
  position: number;
  version: number;
  columns: BoardColumn[];
}

function toBoardCard(raw: RawBoardCard): BoardCard {
  return {
    cardId: raw.cardId,
    columnId: raw.columnId,
    position: raw.position,
    version: raw.version,
    client: toClient(raw.contact),
  };
}

function toBoard(raw: RawBoard): Board {
  return {
    id: raw.id,
    name: raw.name,
    position: raw.position,
    version: raw.version,
    columns: raw.columns.map((col) => ({
      id: col.id,
      name: col.name,
      color: col.color,
      position: col.position,
      version: col.version,
      cards: col.cards.map(toBoardCard),
    })),
  };
}

/**
 * Move um card no draft (Immer) do getBoard — usado no optimistic update do drag.
 * Tira o card da coluna atual e insere na coluna/índice destino, reindexando ambas.
 * Exportado para teste unitário (lógica de reordenação).
 */
export function moveCardInDraft(
  board: Board,
  cardId: string,
  toColumnId: string,
  toIndex: number,
): void {
  let moved: BoardCard | undefined;
  for (const col of board.columns) {
    const idx = col.cards.findIndex((c) => c.cardId === cardId);
    if (idx >= 0) {
      moved = col.cards.splice(idx, 1)[0];
      break;
    }
  }
  if (!moved) return;
  const target = board.columns.find((c) => c.id === toColumnId);
  if (!target) return;
  moved.columnId = toColumnId;
  const at = Math.min(Math.max(toIndex, 0), target.cards.length);
  target.cards.splice(at, 0, moved);
  for (const col of board.columns) {
    col.cards.forEach((c, i) => {
      c.position = i;
    });
  }
}

export const crmApi = api.injectEndpoints({
  endpoints: (build) => ({
    listCarteira: build.query<CursorPage<CrmClient>, ListCarteiraArgs | void>({
      query: (args) =>
        `/crm/carteira${qs({
          segment: args?.segment,
          storeId: args?.storeId,
          q: args?.q,
          sort: args?.sort,
          cursor: args?.cursor,
          limit: args?.limit,
        })}`,
      transformResponse: (raw: Envelope<RawCarteiraItem>) => toCursorPage(raw, toClient),
      serializeQueryArgs: ({ queryArgs }) => ({
        segment: queryArgs?.segment,
        storeId: queryArgs?.storeId,
        q: queryArgs?.q,
        sort: queryArgs?.sort,
      }),
      merge: (current, incoming, { arg }) => {
        if (!arg?.cursor) return incoming;
        const ids = new Set(current.items.map((c) => c.id));
        return {
          items: [...current.items, ...incoming.items.filter((c) => !ids.has(c.id))],
          nextCursor: incoming.nextCursor,
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => currentArg?.cursor !== previousArg?.cursor,
      providesTags: (result) =>
        result
          ? [
              { type: 'CrmClient' as const, id: 'LIST' },
              ...result.items.map((c) => ({ type: 'CrmClient' as const, id: c.id })),
            ]
          : [{ type: 'CrmClient' as const, id: 'LIST' }],
    }),

    getCarteiraSummary: build.query<CarteiraSummary, { storeId?: string } | void>({
      query: (args) => `/crm/carteira/summary${qs({ storeId: args?.storeId })}`,
      transformResponse: toSummary,
      providesTags: [{ type: 'CrmClient' as const, id: 'SUMMARY' }],
    }),

    getCliente: build.query<CrmClientDetail, string>({
      query: (id) => `/crm/clientes/${id}`,
      transformResponse: toDetail,
      providesTags: (_r, _e, id) => [{ type: 'CrmClient' as const, id }],
    }),

    patchCliente: build.mutation<CrmClientDetail, PatchClientInput>({
      query: ({ id, ...body }) => ({ url: `/crm/clientes/${id}`, method: 'PATCH', body }),
      transformResponse: toDetail,
      invalidatesTags: (_r, _e, arg) => [
        { type: 'CrmClient' as const, id: arg.id },
        { type: 'CrmClient' as const, id: 'LIST' },
        { type: 'CrmClient' as const, id: 'SUMMARY' },
        { type: 'CrmReativar' as const, id: 'LIST' },
      ],
    }),

    getFollowUp: build.query<FollowUpItem[], { storeId?: string } | void>({
      query: (args) => `/crm/follow-up${qs({ storeId: args?.storeId })}`,
      transformResponse: (raw: Envelope<RawFollowUpItem>) => raw.data.map(toFollowUp),
      providesTags: [{ type: 'CrmFollowUp' as const, id: 'LIST' }],
    }),
    getFollowUpSummary: build.query<FollowUpSummary, { storeId?: string } | void>({
      query: (args) => `/crm/follow-up/summary${qs({ storeId: args?.storeId })}`,
      providesTags: [{ type: 'CrmFollowUp' as const, id: 'SUMMARY' }],
    }),

    getReativar: build.query<ReactivateItem[], { storeId?: string } | void>({
      query: (args) => `/crm/reativar${qs({ storeId: args?.storeId })}`,
      transformResponse: (raw: Envelope<RawCarteiraItem>) => raw.data.map(toReactivate),
      providesTags: [{ type: 'CrmReativar' as const, id: 'LIST' }],
    }),
    getReativarSummary: build.query<ReactivateSummary, { storeId?: string } | void>({
      query: (args) => `/crm/reativar/summary${qs({ storeId: args?.storeId })}`,
      providesTags: [{ type: 'CrmReativar' as const, id: 'SUMMARY' }],
    }),

    getCampaignTemplates: build.query<CrmTemplates, void>({
      query: () => '/crm/templates',
      transformResponse: toTemplates,
      providesTags: [{ type: 'CrmCampaign' as const, id: 'TEMPLATES' }],
    }),

    listCampaigns: build.query<Campaign[], ListCampaignsInput | void>({
      // storeIds vazio/ausente = todas as lojas visíveis (o backend decide o escopo).
      query: (args) => `/crm/campaigns${qs({ storeIds: args?.storeIds?.join(',') || undefined })}`,
      transformResponse: (raw: Envelope<RawCampaign>) => raw.data.map(toCampaign),
      providesTags: (result) =>
        result
          ? [
              { type: 'CrmCampaign' as const, id: 'LIST' },
              ...result.map((c) => ({ type: 'CrmCampaign' as const, id: c.id })),
            ]
          : [{ type: 'CrmCampaign' as const, id: 'LIST' }],
    }),
    getCampaign: build.query<CampaignDetail, string>({
      query: (id) => `/crm/campaigns/${id}`,
      transformResponse: toCampaignDetail,
      providesTags: (_r, _e, id) => [{ type: 'CrmCampaign' as const, id }],
    }),
    getCampaignTargets: build.query<
      CursorPage<CampaignTarget>,
      { id: string; cursor?: string; limit?: number }
    >({
      query: ({ id, cursor, limit }) => `/crm/campaigns/${id}/targets${qs({ cursor, limit })}`,
      transformResponse: (raw: Envelope<RawCampaignTarget>) => toCursorPage(raw, toCampaignTarget),
      serializeQueryArgs: ({ queryArgs }) => ({ id: queryArgs.id }),
      merge: (current, incoming, { arg }) => {
        if (!arg.cursor) return incoming;
        const ids = new Set(current.items.map((t) => t.id));
        return {
          items: [...current.items, ...incoming.items.filter((t) => !ids.has(t.id))],
          nextCursor: incoming.nextCursor,
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => currentArg?.cursor !== previousArg?.cursor,
      providesTags: (_r, _e, arg) => [{ type: 'CrmCampaign' as const, id: `TARGETS-${arg.id}` }],
    }),
    buildAudience: build.mutation<Audience, BuildAudienceInput>({
      query: (body) => ({ url: '/crm/campaigns/audience', method: 'POST', body }),
      transformResponse: toAudience,
    }),
    listAudienceContacts: build.query<AudienceContactsPage, AudienceContactsInput>({
      query: (body) => ({ url: '/crm/campaigns/audience/contacts', method: 'POST', body }),
      transformResponse: (raw: RawAudienceContacts) => ({
        items: raw.data.map(toClient),
        total: raw.total,
        page: raw.page,
        pageCount: raw.pageCount,
      }),
    }),
    createCampaign: build.mutation<Campaign, CreateCampaignInput>({
      query: (body) => ({ url: '/crm/campaigns', method: 'POST', body }),
      transformResponse: toCampaign,
      invalidatesTags: [
        { type: 'CrmCampaign' as const, id: 'LIST' },
        { type: 'CrmFollowUp' as const, id: 'LIST' },
      ],
    }),
    patchCampaignStatus: build.mutation<Campaign, PatchCampaignStatusInput>({
      query: ({ id, ...body }) => ({ url: `/crm/campaigns/${id}`, method: 'PATCH', body }),
      transformResponse: toCampaign,
      invalidatesTags: (_r, _e, arg) => [
        { type: 'CrmCampaign' as const, id: arg.id },
        { type: 'CrmCampaign' as const, id: 'LIST' },
      ],
    }),

    // --- passos do builder ---
    // Todos invalidam o detalhe: o builder relê o rascunho a cada passo, e a
    // `version` do optimistic lock muda a cada gravação — usar a versão velha no
    // passo seguinte devolveria 409 STALE_ENTITY.
    patchCampaignStore: build.mutation<Campaign, PatchCampaignStoreInput>({
      query: ({ id, ...body }) => ({ url: `/crm/campaigns/${id}/store`, method: 'PATCH', body }),
      transformResponse: toCampaign,
      invalidatesTags: (_r, _e, arg) => campaignTags(arg.id),
    }),
    patchCampaignAudience: build.mutation<Campaign, PatchCampaignAudienceInput>({
      query: ({ id, ...body }) => ({ url: `/crm/campaigns/${id}/audience`, method: 'PATCH', body }),
      transformResponse: toCampaign,
      invalidatesTags: (_r, _e, arg) => campaignTags(arg.id),
    }),
    patchCampaignStrategy: build.mutation<Campaign, PatchCampaignStrategyInput>({
      query: ({ id, ...body }) => ({ url: `/crm/campaigns/${id}/strategy`, method: 'PATCH', body }),
      transformResponse: toCampaign,
      invalidatesTags: (_r, _e, arg) => campaignTags(arg.id),
    }),
    activateCampaign: build.mutation<Campaign, ActivateCampaignInput>({
      query: ({ id, version }) => ({
        url: `/crm/campaigns/${id}/activate`,
        method: 'POST',
        body: { version },
      }),
      transformResponse: toCampaign,
      invalidatesTags: (_r, _e, arg) => campaignTags(arg.id),
    }),
    deleteCampaignDraft: build.mutation<void, string>({
      query: (id) => ({ url: `/crm/campaigns/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => campaignTags(id),
    }),

    // ----------------------------------------------------------- Quadros
    listBoards: build.query<BoardSummary[], void>({
      query: () => '/crm/boards',
      transformResponse: (raw: Envelope<RawBoardSummary>) => raw.data,
      providesTags: (result) =>
        result
          ? [
              { type: 'CrmBoard' as const, id: 'LIST' },
              ...result.map((b) => ({ type: 'CrmBoard' as const, id: b.id })),
            ]
          : [{ type: 'CrmBoard' as const, id: 'LIST' }],
    }),
    getBoard: build.query<Board, string>({
      query: (id) => `/crm/boards/${id}`,
      transformResponse: toBoard,
      providesTags: (_r, _e, id) => [{ type: 'CrmBoard' as const, id }],
    }),
    createBoard: build.mutation<Board, { name: string }>({
      query: (body) => ({ url: '/crm/boards', method: 'POST', body }),
      transformResponse: toBoard,
      invalidatesTags: [{ type: 'CrmBoard' as const, id: 'LIST' }],
    }),
    patchBoard: build.mutation<
      Board,
      { id: string; version: number; name?: string; position?: number }
    >({
      query: ({ id, ...body }) => ({ url: `/crm/boards/${id}`, method: 'PATCH', body }),
      transformResponse: toBoard,
      invalidatesTags: (_r, _e, arg) => [
        { type: 'CrmBoard' as const, id: arg.id },
        { type: 'CrmBoard' as const, id: 'LIST' },
      ],
    }),
    deleteBoard: build.mutation<void, string>({
      query: (id) => ({ url: `/crm/boards/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'CrmBoard' as const, id },
        { type: 'CrmBoard' as const, id: 'LIST' },
      ],
    }),
    addColumn: build.mutation<Board, { boardId: string; name: string; color?: string }>({
      query: ({ boardId, ...body }) => ({
        url: `/crm/boards/${boardId}/columns`,
        method: 'POST',
        body,
      }),
      transformResponse: toBoard,
      invalidatesTags: (_r, _e, arg) => [{ type: 'CrmBoard' as const, id: arg.boardId }],
    }),
    patchColumn: build.mutation<
      Board,
      {
        boardId: string;
        columnId: string;
        version: number;
        name?: string;
        color?: string | null;
        position?: number;
      }
    >({
      query: ({ columnId, boardId: _b, ...body }) => ({
        url: `/crm/boards/columns/${columnId}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: toBoard,
      invalidatesTags: (_r, _e, arg) => [{ type: 'CrmBoard' as const, id: arg.boardId }],
    }),
    deleteColumn: build.mutation<Board, { boardId: string; columnId: string }>({
      query: ({ columnId }) => ({ url: `/crm/boards/columns/${columnId}`, method: 'DELETE' }),
      transformResponse: toBoard,
      invalidatesTags: (_r, _e, arg) => [{ type: 'CrmBoard' as const, id: arg.boardId }],
    }),
    addCard: build.mutation<Board, { boardId: string; columnId: string; contactId: string }>({
      query: ({ boardId, ...body }) => ({
        url: `/crm/boards/${boardId}/cards`,
        method: 'POST',
        body,
      }),
      transformResponse: toBoard,
      invalidatesTags: (_r, _e, arg) => [
        { type: 'CrmBoard' as const, id: arg.boardId },
        { type: 'CrmBoard' as const, id: 'LIST' },
      ],
    }),
    moveCard: build.mutation<
      Board,
      { boardId: string; cardId: string; version: number; toColumnId: string; toIndex: number }
    >({
      query: ({ cardId, version, toColumnId, toIndex }) => ({
        url: `/crm/boards/cards/${cardId}`,
        method: 'PATCH',
        body: { version, toColumnId, toIndex },
      }),
      transformResponse: toBoard,
      async onQueryStarted({ boardId, cardId, toColumnId, toIndex }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          crmApi.util.updateQueryData('getBoard', boardId, (draft) => {
            moveCardInDraft(draft, cardId, toColumnId, toIndex);
          }),
        );
        try {
          const { data } = await queryFulfilled;
          // Reconcilia com o estado autoritativo do servidor (positions/versions).
          dispatch(crmApi.util.updateQueryData('getBoard', boardId, () => data));
        } catch {
          patch.undo();
        }
      },
      // Só os contadores da lista mudam; o getBoard já é reconciliado acima.
      invalidatesTags: [{ type: 'CrmBoard' as const, id: 'LIST' }],
    }),
    removeCard: build.mutation<Board, { boardId: string; cardId: string }>({
      query: ({ cardId }) => ({ url: `/crm/boards/cards/${cardId}`, method: 'DELETE' }),
      transformResponse: toBoard,
      invalidatesTags: (_r, _e, arg) => [
        { type: 'CrmBoard' as const, id: arg.boardId },
        { type: 'CrmBoard' as const, id: 'LIST' },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListCarteiraQuery,
  useGetCarteiraSummaryQuery,
  useGetClienteQuery,
  usePatchClienteMutation,
  useGetFollowUpQuery,
  useGetFollowUpSummaryQuery,
  useGetReativarQuery,
  useGetReativarSummaryQuery,
  useGetCampaignTemplatesQuery,
  useListCampaignsQuery,
  useGetCampaignQuery,
  useGetCampaignTargetsQuery,
  useBuildAudienceMutation,
  useListAudienceContactsQuery,
  useCreateCampaignMutation,
  usePatchCampaignStatusMutation,
  usePatchCampaignStoreMutation,
  usePatchCampaignAudienceMutation,
  usePatchCampaignStrategyMutation,
  useActivateCampaignMutation,
  useDeleteCampaignDraftMutation,
  useListBoardsQuery,
  useGetBoardQuery,
  useCreateBoardMutation,
  usePatchBoardMutation,
  useDeleteBoardMutation,
  useAddColumnMutation,
  usePatchColumnMutation,
  useDeleteColumnMutation,
  useAddCardMutation,
  useMoveCardMutation,
  useRemoveCardMutation,
} = crmApi;
